import { FC } from 'react';
import { Table, Space } from 'antd';
import { ArrowRightOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import FleetTitle from '@/libs/antd/components/Title';
import { useApp } from '@/App';
import { useSimulationFacilities } from '@/libs/fleet-api/hooks/simulation';

/**
 * Type for Simulation Facility Table Row
 */
export interface SimulationFacilityListItem {
	key: number;
	jenisAsset: string;
	kondisiObyek: string;
	jenisPlafon: string;
	sifatLimit: string;
	tenor: string;
	indikasiSukuBunga: string;
	downPayment: string;
	securityDeposit: string;
	biayaAdministrasi: string;
	provisi: string;
	biayaLayanan: string;
	asuransi: string;
	diskonAsuransi: string;
	fidusia: string;
}

type SimulationFacilityListPageProps = {
	spfId: number;
};

/**
 * Simulation Facility List Page
 */
const SimulationFacilityListPage: FC<SimulationFacilityListPageProps> = ({
	spfId,
}) => {
	const { navigate } = useApp();
	const pageSize = 10;
	const pageNumber = 1;

	const { data, isLoading, error } = useSimulationFacilities({
		pagination: { pageSize, pageNumber },
		...(spfId ? { spfId } : {}),
	});

	const tableData = (data?.items || []).map((item) => ({
		spfId: item.spfId,
		facilityId: item.facilityId,
		jenisAsset: item.financingObject,
		kondisiObyek: item.financingObjectCondition,
		jenisPlafon: item.plafonType,
		sifatLimit: item.financingLimitType ?? '-',
		tenor: item.tenor.toString(),
		interestRateIndicationPercent: item.interestRateIndicationPercent + '%',
		downPaymentPercent: item.downPaymentPercent + '%',
		securityDeposit: item.securityDeposit.toLocaleString(),
		biayaAdministrasi: item.administrativeFee.toLocaleString(),
		provisionFeePercent: item.provisionFeePercent + '%',
		serviceFeePercent: item.serviceFeePercent + '%',
		insurance: item.insurance,
		diskonAsuransi: item.insuranceDiscountPercentage ? item.insuranceDiscountPercentage + '%' : (item.insuranceDiscountPercentage ?? '-'),
		fiduciaryFee: item.fiduciaryFee.toLocaleString(),
	}));

	/**
	 * Handles navigation to SimulationPage
	 * @param key Row key (id)
	 */
	const handleCreateSimulasi = (spfId: number, facilityId: number) => {
		try {
			navigate('FRMSimulationPage', { spfId, facilityId });
		} catch (error) {
			console.error('Navigation error:', error);
		}
	};

	return (
		<Space
			direction="vertical"
			style={{ display: 'flex' }}
			size={'large'}
		>
			<div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
				<div>
					<button
						style={{
							display: 'flex',
							height: 44,
							width: 44,
							fontSize: 24,
							border: 'none',
							alignItems: 'center',
							justifyContent: 'center',
							background: '#1557BC',
							color: 'white',
							cursor: 'pointer',
						}}
						onClick={() => {
							navigate('FRMSimulationListPage');
						}}
					>
						<ArrowLeftOutlined />
					</button>
				</div>
				<div style={{ flex: 1 }}>
					<FleetTitle>Simulasi Pembiayaan - Pilih Fasilitas</FleetTitle>
				</div>
			</div>
			<Table
				scroll={{ x: 'max-content' }}
				bordered
				loading={isLoading}
				dataSource={tableData}
				columns={[
					{
						title: 'No',
						width: 1,
						align: 'center',
						render: (_, __, index) => index + 1,
					},
					{ title: 'Jenis Asset', dataIndex: 'jenisAsset' },
					{ title: 'Kondisi Obyek Pembiayaan', dataIndex: 'kondisiObyek' },
					{ title: 'Jenis Plafon', dataIndex: 'jenisPlafon' },
					{ title: 'Sifat Limit Pembiayaan', dataIndex: 'sifatLimit' },
					{ title: 'Tenor Pembiayaan', dataIndex: 'tenor' },
					{
						title: 'Indikasi Suku Bunga (Eff)',
						dataIndex: 'interestRateIndicationPercent',
					},
					{ title: 'Down Payment', dataIndex: 'downPaymentPercent' },
					{ title: 'Security Deposit', dataIndex: 'securityDeposit' },
					{ title: 'Biaya Administrasi', dataIndex: 'biayaAdministrasi' },
					{ title: 'Provisi', dataIndex: 'provisionFeePercent' },
					{ title: 'Biaya Layanan', dataIndex: 'serviceFeePercent' },
					{ title: 'Asuransi', dataIndex: 'insurance' },
					{ title: 'Diskon Asuransi', dataIndex: 'diskonAsuransi' },
					{ title: 'Fidusia', dataIndex: 'fiduciaryFee' },
					{
						title: 'Create Simulasi',
						align: 'center',
						fixed: 'right',
						render: (_, record) => (
							<a
								onClick={(e) => {
									e.preventDefault();
									handleCreateSimulasi(record.spfId, record.facilityId);
								}}
								style={{ color: '#1557BC' }}
								aria-label="Create Simulasi"
							>
								<ArrowRightOutlined />
							</a>
						),
					},
				]}
				pagination={false}
			/>
			{error && (
				<div style={{ color: 'red' }}>
					Gagal memuat data fasilitas simulasi.
				</div>
			)}
		</Space>
	);
};

export default SimulationFacilityListPage;
